import { VoucherCode, VoucherSponsor, VoucherType } from '@products/models/voucher.model';
import { WorkshopsByDay } from '@products/models/workshop.model';

export type TicketPersonId = number;

export type TariffUuid = string;

export type TariffUuids = TariffUuid[];

export enum TariffWarningMessage {
  TARIFF_SOLD_OUT = 'ticket.counter.ticket.slodout',
  WORKSHOPS_SOLD_OUT = 'ticket.counter.workshops.slodout',
  ERROR_TICKET_WARNING = 'ticket-selection.warning',
  ERROR_VOUCHER_WARNING = 'ticket-selection.voucher-warning',
  VOUCHER_LIMITED_WARNING = 'ticket-selection.limited-voucher-warning',
  VOUCHER_ONETIME_WARNING = 'ticket-selection.one-time-voucher-warning',
  TICKET_LIMIT_PER_USER_ACCOUNT_WARNING = 'ticket-selection.ticket-limit-per-user-account-warning'
}

export const enum TariffInfoExpandedTranslationResponse {
  True = 'true',
  False = 'false'
}

export enum TariffCurrencyCodes {
  CHF = 'CHF'
}

export enum TariffClassification {
  Normal = 'normal',
  Parking = 'parking',
  SelfRegistration = 'selfRegistrationOnly'
}

export const enum TariffPersonalizationType {
  Anonymous = 'anonymous',
  Mandatory = 'mandatory'
}

export const enum DocumentUploadType {
  None = 'none',
  Optional = 'optional',
  Mandatory = 'mandatory'
}

export interface TariffType {
  ticketTypeId: number;
  ticketTypeName: string;
  preferredTicketType?: string;
  isVisible: boolean;
  tariffs: Tariff[];
}

export interface Tariff {
  id: number;
  name: string;
  info: string;
  infoExpanded: string;
  price: number;
  ticketPersonTypeId: number;
  ticketPersonId: TicketPersonId;
  ticketTypeId: number;
  ticketTypeName: string;
  tariffNumber: number;
  durationInDays: number;
  availableTickets?: number;
  ticketLimit?: number;
  requiresLegitimation: boolean;
  personalizationType: TariffPersonalizationType;
  documentUploadType: DocumentUploadType;
  classification: TariffClassification;
  preferredTicket?: boolean;
  isVisible: boolean;
  isParkingTicket: boolean;
  isLicensePlateMandatory: boolean;
  isVoucher: boolean;
  sponsors?: VoucherSponsor[];
  voucherCode?: VoucherCode;
  voucherType?: VoucherType;
  voucherCountLimit?: number;
  activatedTimestamp?: number;
  releasedInMinutes?: number;
  allowedWorkshops?: number[];
  allowedWorkshopsFull?: boolean;
  workshopsByDay: WorkshopsByDay[];
  hasDaySellLimit?: boolean;
  validFrom?: string;
  validTill?: string;
  daysOffset: number;
  shouldCalendarBeDisplayed: boolean;
  hasUserAccountTicketLimit: boolean;
  packageSettings: TariffPackageSettings | null;
  packageNumber: number | null;
  packageIndex: number | null;
}

export interface TariffPackageSettings {
  useTariffSheetPrice: boolean;
  fixedAmount: boolean;
  amount: number;
  minAmount?: number;
  maxAmount?: number;
  packageItemPrice?: number;
}

export interface TariffValidationStateViewModel {
  count: number;
  countDifference: number;
  validatedCount: number;
  previousValidatedCount: number;
  bookedCount: number;
  validatedMaxLimit: number;
  isCountOverLimit: boolean;
  isTariffSoldOut: boolean;
  isAvailableTariffsLimitReached: boolean;
  numberOfAllBookedTariffs: number;
  numberOfAllBookedCurrentTariffs: number;
  initialNumberOfAvailableTariffs: number;
  currentNumberOfAvailableTariffs: number;
  percentageOfAvailableTariffs: number;
  tariffLimit: number;
  maxTariffLimit: number;
  tariffLimitPerUserAccount: number;
  tariffSpecificLimitPerUserAccount: number;
  currentUserAccountTariffLimit: number;
  isCurrentUserAccountTariffLimitReached: boolean;
  voucherCountLimit: number;
  validateVoucherCountLimit: boolean;
  maxVoucherLimit: number;
  maxLimitedVoucherLimit: number;
  numberOfBookedVouchers: number;
  numberOfBookedLimitedVouchers: number;
  numberOfBookedOneTimeVouchers: number;
  isTariffClassificationNormal: boolean;
  isTariffClassificationParking: boolean;
  allowedWorkshopsSeatsAvailable: number;
  hideWorkshopTariffCounter: boolean;
  areAllAllowedWorkshopsAssigned: boolean;
  isWorkshopsSelectionMandatoryAndHasWorkshops: boolean;
  isVoucher: boolean;
  isVoucherTypePromoCode: boolean;
  isVoucherTypeLimitedPromoCode: boolean;
  isVoucherTypeOneTimeVoucher: boolean;
  isVoucherIncludedPerUserAccountLimit: boolean;
  isVoucherLimitReached: boolean;
  isPackageFixedAmountLimitReached: boolean;
  isPackageMinAmountLimitReached: boolean;
  isPackageMaxAmountLimitReached: boolean;
  isPackageTypeAndIsPackageNotAdded: boolean;
  packageSettings: TariffPackageSettings;
  packageTooltipMessage: string;
  showMobilePackageTooltipMessage: boolean;
  tariffLimitWarningMessage: string;
}

export interface PreferredTariffValidationState extends TariffValidationStateViewModel {
  ticketTypeId: number;
  ticketPersonTypeId: number;
}
